import React, { useState, useEffect, useRef } from 'react'
import {
    FormGroup,
    Label,
    Input,
    FormText,
    Badge,
    Popover,
    PopoverHeader,
    PopoverBody,
} from 'reactstrap'
import Select from 'react-select'

const ActionConfig = ({ nodeData, nodeId, actionTypesAndParams, availableModel, onUpdate, onDelete }) => {
    const [actionType, setActionType] = useState(nodeData.actionType || '')
    const [actionParamsDef, setActionParamsDef] = useState([])
    const [actionParamsValues, setActionParamsValues] = useState(nodeData.actionParams || {})
    const [openModal, setOpenModal] = useState('')
    const [currentCursorPosition, setCurrentCursorPosition] = useState(0)
    const inputRefs = useRef({})
    const allAutomatableFields = availableModel?.allAutomatableFields || []

    useEffect(() => {
        setActionType(nodeData.actionType || '')
        setActionParamsValues(nodeData.actionParams || {})
    }, [nodeData])

    useEffect(() => {
        if (actionType && actionTypesAndParams) {
            const match = actionTypesAndParams.find((a) => a.actionType === actionType.toLowerCase())
            if (match) {
                try {
                    setActionParamsDef(JSON.parse(match.actionParams))
                } catch {
                    setActionParamsDef([])
                }
            }
        }
    }, [actionType, actionTypesAndParams])

    const pushUpdate = (type, params) => {
        onUpdate({ actionType: type, actionParams: params })
    }

    const handleActionTypeChange = (opt) => {
        const type = opt.value.actionType
        let paramsDef = []
        try {
            paramsDef = JSON.parse(opt.value.actionParams)
        } catch { /* ignore */ }
        setActionType(type)
        setActionParamsDef(paramsDef)

        const defaults = {}
        paramsDef.forEach((p) => { defaults[p.name] = p.default || '' })
        setActionParamsValues(defaults)
        pushUpdate(type, defaults)
    }

    const handleParamChange = (field, value) => {
        const next = { ...actionParamsValues, [field]: value }
        setActionParamsValues(next)
        pushUpdate(actionType, next)
    }

    const handleParamInputChange = (paramName, e) => {
        const value = e.target.value
        const cursorPos = e.target.selectionStart
        handleParamChange(paramName, value)
        setCurrentCursorPosition(cursorPos)
        if (cursorPos >= 2 && value.slice(cursorPos - 2, cursorPos) === '{{') {
            setOpenModal(`Popover${paramName}`)
        }
    }

    const handleInstanceFieldClick = (paramName, field) => {
        const value = actionParamsValues[paramName] || ''
        const insertion = `instance.${field}}}`
        const newValue = `${value.slice(0, currentCursorPosition)}${insertion}${value.slice(currentCursorPosition)}`
        const next = { ...actionParamsValues, [paramName]: newValue }
        setActionParamsValues(next)
        pushUpdate(actionType, next)
        setOpenModal('')
        const inputRef = inputRefs.current[paramName]
        if (inputRef) {
            const insertEnd = currentCursorPosition + insertion.length
            inputRef.focus()
            inputRef.setSelectionRange(insertEnd, insertEnd)
        }
    }

    const actionTypeOptions = (actionTypesAndParams || []).map((item) => ({
        label: item.actionType?.replaceAll('_', ' ').toUpperCase(),
        value: item,
    }))

    const currentTypeValue = actionType
        ? { label: actionType.replaceAll('_', ' ').toUpperCase(), value: actionType }
        : null

    return (
        <div className="wf-config-form">
            <div className="wf-section">
                <div className="wf-section-label">Type</div>
                <FormGroup>
                    <Select
                        className="bos-custom-select"
                        classNamePrefix="bos-select"
                        options={actionTypeOptions}
                        value={currentTypeValue}
                        placeholder="Select action type..."
                        onChange={handleActionTypeChange}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </FormGroup>
            </div>

            {actionType && actionParamsDef.length > 0 && (
                <div className="wf-section">
                    <div className="wf-section-label">Parameters</div>

                    <div className="wf-template-hint">
                        <i className="fa fa-magic" />
                        <span>Type <code>{"{{"}</code> in any field to insert dynamic values from the triggering object.</span>
                    </div>

                    {actionParamsDef.map((param) => (
                        <FormGroup key={param.name}>
                            <Label>
                                {param.name.replaceAll('_', ' ').toUpperCase()}
                                {param.is_required && <span className="wf-required">*</span>}
                            </Label>
                            {param.description && <FormText color="muted">{param.description}</FormText>}

                            {param.input === 'input' || param.input === 'text' ? (
                                <Input
                                    type="text"
                                    id={`panel-${param.name}-actionParam`}
                                    value={actionParamsValues[param.name] || ''}
                                    required={param.is_required}
                                    onChange={(e) => handleParamInputChange(param.name, e)}
                                    onClick={(e) => setCurrentCursorPosition(e.target.selectionStart)}
                                    innerRef={(el) => { inputRefs.current[param.name] = el }}
                                />
                            ) : param.input === 'textarea' ? (
                                <Input
                                    type="textarea"
                                    id={`panel-${param.name}-actionParam`}
                                    value={actionParamsValues[param.name] || ''}
                                    required={param.is_required}
                                    rows={3}
                                    onChange={(e) => handleParamInputChange(param.name, e)}
                                    onClick={(e) => setCurrentCursorPosition(e.target.selectionStart)}
                                    innerRef={(el) => { inputRefs.current[param.name] = el }}
                                />
                            ) : param.input === 'select' ? (
                                <Input
                                    type="select"
                                    value={actionParamsValues[param.name] || ''}
                                    required={param.is_required}
                                    onChange={(e) => handleParamChange(param.name, e.target.value)}
                                >
                                    <option value="">-- Select --</option>
                                    {(param.choices || []).map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </Input>
                            ) : param.input === 'date' ? (
                                <Input
                                    type="date"
                                    value={actionParamsValues[param.name] || ''}
                                    required={param.is_required}
                                    onChange={(e) => handleParamChange(param.name, e.target.value)}
                                />
                            ) : (
                                <Input
                                    type="text"
                                    id={`panel-${param.name}-actionParam`}
                                    value={actionParamsValues[param.name] || ''}
                                    onChange={(e) => handleParamInputChange(param.name, e)}
                                    onClick={(e) => setCurrentCursorPosition(e.target.selectionStart)}
                                    innerRef={(el) => { inputRefs.current[param.name] = el }}
                                />
                            )}
                        </FormGroup>
                    ))}
                </div>
            )}

            {openModal && availableModel && allAutomatableFields.length > 0 && (
                <Popover
                    placement="left"
                    target={`panel-${openModal.replace('Popover', '')}-actionParam`}
                    isOpen={true}
                    toggle={() => setOpenModal('')}
                >
                    <PopoverHeader>
                        Insert {availableModel.contentType?.model} field
                    </PopoverHeader>
                    <PopoverBody style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {allAutomatableFields.map((field) => (
                            <Badge
                                key={field}
                                className="text-dark m-1 p-2"
                                style={{ cursor: 'pointer', fontSize: 10 }}
                                color={field.includes('.') ? 'info' : 'warning'}
                                onClick={() => handleInstanceFieldClick(openModal.replace('Popover', ''), field)}
                            >
                                {field.replaceAll('_', ' ').toUpperCase()}
                            </Badge>
                        ))}
                    </PopoverBody>
                </Popover>
            )}

            <div className="wf-section wf-section-danger">
                <button className="wf-delete-btn" onClick={() => onDelete(nodeId)}>
                    <i className="fa fa-trash" /> Remove Action
                </button>
            </div>
        </div>
    )
}

export default ActionConfig
